import {Directive, EventEmitter, Input, Output} from '@angular/core';
import {SortableHeaderDirective, SortableType} from './sortable-header.directive';

export type SortOrder = 'asc' | 'desc';

@Directive({
  selector: '[appSortableTable]',
  standalone: true
})
export class SortableTableDirective {
  @Output() appSortableTableChange = new EventEmitter<any[]>();

  private rowsRef: any[] = [];
  private lastEmitted: any[] | null = null;
  private headers: SortableHeaderDirective[] = [];

  activeKey: string | null = null;
  activeOrder: SortOrder | null = null;

  @Input()
  set appSortableTable(value: any[]) {
    const next = value ?? [];

    // Skip the echo from our own emit (two-way binding round-trip).
    if (next === this.lastEmitted) {
      this.rowsRef = next;
      return;
    }

    this.rowsRef = next;

    // New data set from the outside: drop sort state and clear icons.
    this.activeKey = null;
    this.activeOrder = null;
    this.headers.forEach(h => h.updateState(null));
  }

  get appSortableTable(): any[] {
    return this.rowsRef;
  }

  register(header: SortableHeaderDirective): void {
    this.headers.push(header);
  }

  unregister(header: SortableHeaderDirective): void {
    this.headers = this.headers.filter(h => h !== header);
  }

  sortBy(key: string, type: SortableType): void {
    const order: SortOrder =
      this.activeKey === key && this.activeOrder === 'desc' ? 'asc' : 'desc';

    this.activeKey = key;
    this.activeOrder = order;

    this.headers.forEach(h =>
      h.updateState(h.appSortable === key ? order : null)
    );

    const sorted = [...this.rowsRef].sort((a, b) => {
      const av = SortableTableDirective.normalize(SortableTableDirective.resolve(a, key), type);
      const bv = SortableTableDirective.normalize(SortableTableDirective.resolve(b, key), type);

      if (av < bv) {
        return order === 'asc' ? -1 : 1;
      }
      if (av > bv) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    this.rowsRef = sorted;
    this.lastEmitted = sorted;
    this.appSortableTableChange.emit(sorted);
  }

  private static resolve(row: any, key: string): any {
    if (row == null) {
      return undefined;
    }
    if (!key.includes('.')) {
      return row[key];
    }
    return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row);
  }

  private static normalize(value: any, type: SortableType): any {
    if (value === null || value === undefined || value === '') {
      return type === 'number' ? Number.NEGATIVE_INFINITY : '';
    }

    if (type === 'number') {
      if (typeof value === 'number') {
        return value;
      }

      // Strip currency symbols, percent signs, spaces; accept comma decimal.
      const cleaned = String(value)
        .replace(/[%$€£¥₹]/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');

      const num = parseFloat(cleaned);
      return isNaN(num) ? Number.NEGATIVE_INFINITY : num;
    }

    return String(value).toLowerCase();
  }
}
